import {useState, useEffect} from 'react';
import {useParams} from "react-router";
import {
    getBatch, getHarvestedPlantsByBatch, getPlantTrimByBatch,
    getBatchNotes, updateBatch, updateBatchDescription
} from "../../api/Api";
import {Card, Divider, Row, Col} from "antd";
import {toFSDate} from "../../util/timeutils";
import {BasicPlantTable} from "./BasicPlantTable";
import {BasicTrimTable} from "./BasicTrimTable";
import {CultivationBonusCard} from "./CultivationBonusCard";
import {checkPerm} from "../../auth/sessionTools";
import {BatchNotesTable} from "./BatchNotesTable";
import {handleNoSession} from "../../util/errorUtils";
import {NewBatchNotesWidget} from "./NewBatchNotesWidget";
import {DatePickerWidget} from "../widget/DatePickerWidget";
import {RoomSelectWidget} from "./RoomSelectWidget";
import {TextChangeWidget} from "../widget/TextChangeWidget";

const handleLogout = (e, onApiFail) => {
    if ((e.status === 401) && (onApiFail)) {
        onApiFail();
    }
}

export const BatchView = ({session, roomMap, refresh, onRefresh, onApiFail}) => {
    const params = useParams();
    const {batchId} = params;
    const [batch, setBatch] = useState(undefined);
    const [plants, setPlants] = useState([]);
    const [trim, setTrim] = useState([]);
    const [notes, setNotes] = useState([]);
    const io = {onApiFail, onRefresh, refresh};

    useEffect(() => {
        getBatch(batchId).then(r => {
            setBatch(r);
        }).catch(e => {
            handleLogout(e, onApiFail);
        });

        getHarvestedPlantsByBatch(batchId).then(r => {
            setPlants(r.map(plant => {
                return {
                    ...plant,
                    key: plant.id
                }
            }))
        }).catch(e => {
            handleLogout(e, onApiFail);
        });

        getPlantTrimByBatch(batchId).then(r => {
            // put it in a map, separate out trim that has inventory
            // const nextMap = {};
            // for (let i=0; i<r.length; i++) {
            //     const t = r[i];
            //     if (nextMap[t.productId] === undefined) {
            //         nextMap[t.productId] = {
            //             ...t,
            //             key: i
            //         }
            //     } else {
            //         nextMap[t.productId].amount += t.amount;
            //     }
            // }
            // setTrim(Object.values(nextMap));
            setTrim(r.map((row, index) => {
                return {
                    ...row,
                    key: index
                }
            }))
        }).catch(e => {
            handleLogout(e, onApiFail);
        });

        getBatchNotes(batchId).then(r => {
            setNotes(r.map(note => {
                return {...note, key: note.id}
            }))
        }).catch(e => {
            handleNoSession(e, onApiFail);
        })

    }, [batchId, refresh, onApiFail]);

    const onDescriptionChange = (val) => {
        updateBatchDescription(batchId, val).then(r => {
            if (onRefresh) { onRefresh(); }
        }).catch(e => {
            handleNoSession(e, onApiFail);
        })
    }

    const onStartTimeChange = (dateString, dateJS) => {
        updateBatch(batchId, {startTime: dateJS.toString()}).then(r => {
            if (onRefresh) { onRefresh(); }
        }).catch(e => {
            handleNoSession(e, onApiFail);
        });
    }

    const onHarvestTimeChange = (dateString, dateJS) => {
        updateBatch(batchId, {harvestTime: dateJS.toString()}).then(r => {
            if (onRefresh) { onRefresh(); }
        }).catch(e => {
            handleNoSession(e, onApiFail);
        });
    }

    const onFlowerRoomSelect = (value) => {
        updateBatch(batchId, {flowerRoomId: value.id}).then(r => {
            if (onRefresh) { onRefresh(); }
        }).catch(e => {
            handleNoSession(e, onApiFail);
        });
    }

    const onHarvestRoomSelect = (value) => {
        updateBatch(batchId, {harvestRoomId: value.id}).then(r => {
            if (onRefresh) { onRefresh(); }
        }).catch(e => {
            handleNoSession(e, onApiFail);
        });
    }

    const cardStyle={
        textAlign: 'left'
    }

    const rowStyle = {
        marginTop: '5px',
        marginBottom: '5px'
    }

    if (batch === undefined) {
        return <div/>
    }

    const {name, created, description, startTime, harvestTime, flowerRoomId, harvestRoomId} = batch;
    const showBonus = checkPerm(session, "executive") || checkPerm(session, "accounting") || checkPerm(session, "cultivation")

    return <div>
        <Card style={cardStyle} title={<b>{batch.name}</b>}>
            <Row style={rowStyle}>
                <Col span={2}><b>Name:</b></Col>
                <Col>{name}</Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2}><b>Description:</b></Col>
                <Col span={8}>
                    <TextChangeWidget text={description} placeholder={'Description'} rows={4} maxLength={2048} onTextSave={onDescriptionChange} />
                </Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2}><b>Created:</b></Col>
                <Col>{toFSDate(new Date(created))}</Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2} style={{marginTop: '5px'}}><b>Start Time:</b></Col>
                <Col><DatePickerWidget date={startTime} placeholder={'Start Time'} onChange={onStartTimeChange} /></Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2} style={{marginTop: '5px'}}><b>Harvest Time:</b></Col>
                <Col><DatePickerWidget date={harvestTime} placeholder={'Harvest Time'} onChange={onHarvestTimeChange} /></Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2} style={{marginTop: '5px'}}><b>Flower Room:</b></Col>
                <Col><RoomSelectWidget onSelect={onFlowerRoomSelect} placeholder={'Flower Room'} roomId={flowerRoomId} roomMap={roomMap} type={'flower'} /></Col>
            </Row>
            <Row style={rowStyle}>
                <Col span={2} style={{marginTop: '5px'}}><b>Harvest Room:</b></Col>
                <Col><RoomSelectWidget onSelect={onHarvestRoomSelect} placeholder={'Harvest Room'} roomId={harvestRoomId} roomMap={roomMap} type={'harvest'} /></Col>
            </Row>
        </Card>
        {(plants.length > 0) &&
            <Card style={cardStyle} title={<b>Harvested Plants</b>}>
                <BasicPlantTable plants={plants} showGeneration={false} showDestroyed={true} />
            </Card>
        }
        {(trim.length > 0) &&
            <Card style={cardStyle} title={<b>Trim Products</b>}>
                <BasicTrimTable trim={trim} />
            </Card>
        }
        <Card style={cardStyle} title={<b>Notes</b>}>
            <BatchNotesTable session={session} notes={notes} io={io} />
            <Divider />
            <NewBatchNotesWidget batch={batch} io={io} />
        </Card>
        {showBonus && (trim.length > 0) &&
            <CultivationBonusCard trim={trim} />
        }
    </div>
}